import {useGetPatientTherapies} from '@/modules/patients/api/use-get-patient-therapies'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Box, Button, ButtonGroup, HStack, Text, Textarea, useDialogContext, VStack} from '@chakra-ui/react'
import {PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {PatientProtocol} from '@cypro/engine/queries'
import {DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {toaster} from '@cypro/ui/toaster'
import {isSequenceBeforeProduction} from '@cypro/engine/utils'
import {useTranslate} from '@tolgee/react'
import {useMemo, useState} from 'react'

export interface StopProtocolDialogProps {
  patientProtocol: PatientProtocol
}

export function StopProtocolDialog({patientProtocol}: StopProtocolDialogProps) {
  const {t} = useTranslate('common')
  const context = useDialogContext()
  const zero = useZero()
  const [reason, setReason] = useState('')
  const [isStopping, setIsStopping] = useState(false)
  const [patientTherapies] = useGetPatientTherapies(patientProtocol.patientId)

  const sequences = useMemo(
    () => patientTherapies.filter(seq => seq.patientProtocolId === patientProtocol.id && !seq.isNote),
    [patientTherapies, patientProtocol.id]
  )

  const totalCycles = patientProtocol.cycles ?? patientProtocol.protocol?.totalCycles ?? 1
  const startedCycle = sequences.reduce((max, seq) => {
    const isStarted =
      seq.status === PatientTherapySequenceStatus.enum.in_production ||
      seq.status === PatientTherapySequenceStatus.enum.administered
    return isStarted && seq.cycle != null && seq.cycle > max ? seq.cycle : max
  }, 1)
  const prescriptionsToCancel = sequences.filter(isSequenceBeforeProduction).length

  function onKeep() {
    context.setOpen(false)
  }

  function onStop() {
    setIsStopping(true)
    void zero.attemptMutation(
      zero.mutators.patients.stopProtocol({patientProtocolId: patientProtocol.id, reason: reason || undefined}),
      async mutation => {
        const data = await mutation.server
        if (!data) {
          setIsStopping(false)
          return
        }
        context.setOpen(false)
        toaster.success({title: t('success'), description: t('patient_updated_successfully')})
      }
    )
  }

  return (
    <>
      <DialogHeader>
        <Text fontSize="lg" fontWeight="semibold">
          {t('stop_protocol_confirm', 'Stop protocol?')}
        </Text>
      </DialogHeader>

      <DialogBody minHeight="none">
        <VStack align="stretch" gap="4" width="100%">
          <Text fontSize="sm" color="fg.muted">
            {t(
              'stop_protocol_description',
              'The patient will not continue this protocol. Cycles that have started keep their history.'
            )}
          </Text>

          <Box bg="bg.subtle" borderRadius="md" borderWidth="1px" borderColor="border" p="4">
            <VStack align="stretch" gap="2">
              <HStack gap="2">
                <Text fontSize="sm" color="fg.muted">
                  {t('protocol')}:
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {patientProtocol.protocol?.name ?? '-'}
                </Text>
              </HStack>
              <HStack gap="2">
                <Text fontSize="sm" color="fg.muted">
                  {t('stopped_after', 'Stopped after')}:
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {t('cycle_x_of_y', 'cycle {{cycle}} of {{total}}', {cycle: startedCycle, total: totalCycles})}
                </Text>
              </HStack>
              <HStack gap="2">
                <Text fontSize="sm" color="fg.muted">
                  {t('prescriptions_to_cancel', 'Prescriptions to cancel')}:
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {prescriptionsToCancel}
                </Text>
              </HStack>
            </VStack>
          </Box>

          <Text fontSize="sm" color="fg.muted">
            {t(
              'stop_protocol_cancellation_notice',
              'Prescriptions not yet in production are cancelled. Prescriptions already in production, at QA or released are not changed.'
            )}
          </Text>

          <Box>
            <Text fontWeight="medium" marginBottom="2">
              {t('reason_optional', 'Reason (optional)')}
            </Text>
            <Textarea
              placeholder={t('recorded_in_patient_history', 'Recorded in the patient history')}
              value={reason}
              onChange={e => {
                setReason(e.target.value)
              }}
              rows={3}
            />
          </Box>
        </VStack>
      </DialogBody>

      <DialogFooter>
        <ButtonGroup size="md" width="100%" justifyContent="flex-end">
          <Button variant="outline" colorPalette="gray" onClick={onKeep} disabled={isStopping}>
            {t('keep_protocol', 'Keep protocol')}
          </Button>
          <Button colorPalette="red" onClick={onStop} loading={isStopping}>
            {t('stop_protocol', 'Stop protocol')}
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </>
  )
}
